/* ImageEditor — full-featured photo adjustment modal.

   Features:
     • Pan (drag inside the crop area)
     • Zoom in/out (mouse wheel + slider)
     • Rotate 90° increments
     • Crop with optional fixed aspect ratio (cover, square, etc.)
     • Brightness + contrast sliders applied via CSS filter
     • Outputs a Blob (image/jpeg) ready to upload, plus the source `name`
       so callers can drop it straight into existing FormData handlers.

   Usage:
     <ImageEditor
       open={open}
       file={file}                      // File | Blob
       aspect={16 / 9}                  // null = freeform; or number
       title="Adjust your cover photo"
       onCancel={() => setOpen(false)}
       onSave={(blob) => upload(blob)}  // blob: File w/ same name as input
     />
*/
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Cropper from 'react-easy-crop';
import { RotateCw, ZoomIn, ZoomOut, Move, Sliders, X, Check } from 'lucide-react';

/**
 * @param {object} pixelCrop  {x, y, width, height} in source image pixels
 * @param {HTMLImageElement} image
 * @param {number} rotation  degrees (0/90/180/270)
 * @param {number} brightness  100 = normal
 * @param {number} contrast    100 = normal
 * @returns {Promise<Blob>}
 */
async function renderCroppedBlob(image, pixelCrop, rotation, brightness, contrast, mime) {
  // Step 1 — render the rotated source into a working canvas. Easier
  // than computing rotated pixel coords by hand and matches what the
  // user sees in the preview.
  const safeArea = 2 * Math.max(image.width, image.height);
  const workCanvas = document.createElement('canvas');
  workCanvas.width = safeArea; workCanvas.height = safeArea;
  const wctx = workCanvas.getContext('2d');
  wctx.translate(safeArea / 2, safeArea / 2);
  wctx.rotate((rotation * Math.PI) / 180);
  wctx.translate(-image.width / 2, -image.height / 2);
  wctx.drawImage(image, 0, 0);

  const data = wctx.getImageData(0, 0, safeArea, safeArea);

  // Step 2 — crop to the requested rect in the rotated frame.
  const out = document.createElement('canvas');
  out.width = pixelCrop.width;
  out.height = pixelCrop.height;
  const octx = out.getContext('2d');
  octx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
    Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y),
  );

  // Step 3 — apply brightness/contrast via a second pass canvas because
  // filter() applied during drawImage works in browsers but is slow
  // server-side; doing it on the final-size canvas keeps it fast.
  if (brightness !== 100 || contrast !== 100) {
    const filtered = document.createElement('canvas');
    filtered.width = out.width; filtered.height = out.height;
    const fctx = filtered.getContext('2d');
    fctx.filter = `brightness(${brightness}%) contrast(${contrast}%)`;
    fctx.drawImage(out, 0, 0);
    return new Promise((resolve) => filtered.toBlob(resolve, mime || 'image/jpeg', 0.92));
  }
  return new Promise((resolve) => out.toBlob(resolve, mime || 'image/jpeg', 0.92));
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export function ImageEditor({
  open,
  file,
  aspect = null,             // null = freeform; or e.g. 16/9, 1/1, 4/3
  title = 'Adjust your photo',
  saveLabel = 'Save & Use',
  outputMime,                 // default image/jpeg
  onCancel,
  onSave,                     // (blob: Blob, name: string) => void
}) {
  const [imageUrl, setImageUrl] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  // Reset when a new file comes in.
  useEffect(() => {
    if (!file) {
      setImageUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setBrightness(100);
    setContrast(100);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const filterStyle = useMemo(() => ({
    filter: `brightness(${brightness}%) contrast(${contrast}%)`,
  }), [brightness, contrast]);

  const handleSave = async () => {
    if (!imageUrl || !croppedAreaPixels) return;
    setSaving(true);
    try {
      const img = await loadImage(imageUrl);
      const mime = outputMime || file?.type || 'image/jpeg';
      const blob = await renderCroppedBlob(
        img, croppedAreaPixels, rotation, brightness, contrast,
        mime === 'image/png' ? 'image/png' : 'image/jpeg',
      );
      const fileName = file?.name || 'photo.jpg';
      // Promote the blob to a File so the existing FormData upload
      // pipelines treat it identically to a fresh file upload.
      const outFile = new File([blob], fileName, {
        type: blob.type, lastModified: Date.now(),
      });
      onSave?.(outFile, fileName);
    } catch (e) {
      console.error('image render failed', e);
    } finally { setSaving(false); }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="image-editor-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}
    >
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        <header className="px-5 py-3.5 flex items-center justify-between border-b border-slate-200">
          <div className="font-serif text-lg text-[hsl(var(--primary))]">{title}</div>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-slate-100 rounded-full text-slate-600"
            aria-label="Cancel"
            data-testid="image-editor-cancel-x"
          ><X size={18} /></button>
        </header>

        {/* Crop canvas */}
        <div className="relative bg-slate-900 h-[55vh] min-h-[340px]" style={filterStyle}>
          {imageUrl && (
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect || undefined}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
              cropShape="rect"
              showGrid={true}
              restrictPosition={false}
              style={{ containerStyle: { background: '#0f172a' } }}
            />
          )}
        </div>

        {/* Controls */}
        <div className="px-5 py-4 space-y-3 bg-slate-50">
          <ControlRow icon={<ZoomIn size={14} />} label="Zoom">
            <input
              type="range" min={1} max={3} step={0.01}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="flex-1"
              data-testid="image-editor-zoom"
              aria-label="Zoom"
            />
            <button onClick={() => setZoom(Math.max(1, zoom - 0.1))} className="p-1.5 hover:bg-slate-200 rounded" aria-label="Zoom out"><ZoomOut size={14}/></button>
            <button onClick={() => setZoom(Math.min(3, zoom + 0.1))} className="p-1.5 hover:bg-slate-200 rounded" aria-label="Zoom in"><ZoomIn size={14}/></button>
          </ControlRow>
          <ControlRow icon={<RotateCw size={14} />} label="Rotate">
            <input
              type="range" min={0} max={360} step={1}
              value={rotation}
              onChange={(e) => setRotation(parseInt(e.target.value, 10))}
              className="flex-1"
              data-testid="image-editor-rotation"
              aria-label="Rotation"
            />
            <button onClick={() => setRotation((rotation + 90) % 360)} className="p-1.5 hover:bg-slate-200 rounded text-xs px-2" aria-label="Rotate 90°">+90°</button>
          </ControlRow>
          <ControlRow icon={<Sliders size={14} />} label="Brightness">
            <input
              type="range" min={50} max={150} step={1}
              value={brightness}
              onChange={(e) => setBrightness(parseInt(e.target.value, 10))}
              className="flex-1"
              data-testid="image-editor-brightness"
              aria-label="Brightness"
            />
            <button onClick={() => setBrightness(100)} className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 hover:text-slate-800">Reset</button>
          </ControlRow>
          <ControlRow icon={<Sliders size={14} />} label="Contrast">
            <input
              type="range" min={50} max={150} step={1}
              value={contrast}
              onChange={(e) => setContrast(parseInt(e.target.value, 10))}
              className="flex-1"
              data-testid="image-editor-contrast"
              aria-label="Contrast"
            />
            <button onClick={() => setContrast(100)} className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 hover:text-slate-800">Reset</button>
          </ControlRow>
        </div>

        {/* Footer */}
        <footer className="px-5 py-3.5 bg-white border-t border-slate-200 flex items-center justify-between gap-3">
          <div className="text-[11px] text-slate-500 hidden sm:flex items-center gap-1">
            <Move size={12}/> Drag the image to reposition · scroll to zoom
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={onCancel}
              className="px-4 py-2 rounded-lg text-slate-700 hover:bg-slate-100 text-sm"
              data-testid="image-editor-cancel"
            >Cancel</button>
            <button
              onClick={handleSave}
              disabled={saving || !croppedAreaPixels}
              className="ml-btn-gold !py-2 !px-4 text-sm"
              data-testid="image-editor-save"
            >
              <Check size={14}/> {saving ? 'Saving…' : saveLabel}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function ControlRow({ icon, label, children }) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <div className="flex items-center gap-2 w-24 text-slate-700 font-medium shrink-0">
        {icon} {label}
      </div>
      {children}
    </div>
  );
}

export default ImageEditor;
